import React, { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#C75B3F', '#1B2B4B', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function ArticleChart({ config }) {
  const { t } = useLanguage();
  const { type = 'bar', title, data, xKey = 'name', yKey = 'value', yKeys, colors } = config;
  const chartColors = colors || CHART_COLORS;

  if (!data || !Array.isArray(data) || data.length === 0) return null;

  const allYKeys = yKeys || [yKey];

  return (
    <div className="my-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700" data-testid="article-chart">
      {title && <h4 className="text-sm font-bold text-navy dark:text-white mb-4 text-center">{title}</h4>}
      <ResponsiveContainer width="100%" height={320}>
        {type === 'bar' ? (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            {allYKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {allYKeys.map((key, i) => (
              <Bar key={key} dataKey={key} fill={chartColors[i % chartColors.length]} radius={[4, 4, 0, 0]} />
            ))}
          </BarChart>
        ) : type === 'line' ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            {allYKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {allYKeys.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key} stroke={chartColors[i % chartColors.length]} strokeWidth={2} dot={{ r: 3 }} />
            ))}
          </LineChart>
        ) : type === 'area' ? (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            {allYKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {allYKeys.map((key, i) => (
              <Area key={key} type="monotone" dataKey={key} stroke={chartColors[i % chartColors.length]} fill={chartColors[i % chartColors.length]} fillOpacity={0.2} />
            ))}
          </AreaChart>
        ) : type === 'pie' ? (
          <PieChart>
            <Pie data={data} dataKey={yKey} nameKey={xKey} cx="50%" cy="50%" outerRadius={120} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
              {data.map((_, i) => (
                <Cell key={i} fill={chartColors[i % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        ) : null}
      </ResponsiveContainer>
    </div>
  );
}

export default function DataVisualization({ content }) {
  const charts = useMemo(() => {
    if (!content) return [];
    const parsed = [];
    // Match <!-- chart:{ ... } --> comment blocks in HTML
    const chartPattern = /<!--\s*chart:([\s\S]*?)-->/g;
    let match;
    while ((match = chartPattern.exec(content)) !== null) {
      try {
        const config = JSON.parse(match[1].trim());
        parsed.push(config);
      } catch (e) {
        // Skip invalid chart configs
      }
    }
    // Also match <div data-chart='...'></div> blocks
    const divPattern = /<div[^>]*data-chart=['"]([\s\S]*?)['"][^>]*>[\s\S]*?<\/div>/g;
    while ((match = divPattern.exec(content)) !== null) {
      try {
        const decoded = match[1].replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
        const config = JSON.parse(decoded);
        parsed.push(config);
      } catch (e) {
        // Skip invalid
      }
    }
    return parsed;
  }, [content]);

  if (charts.length === 0) return null;

  return (
    <div data-testid="article-data-visualizations">
      {charts.map((chart, i) => (
        <ArticleChart key={i} config={chart} />
      ))}
    </div>
  );
}
